import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import { CreateQuestionForm } from '../CreateQuestionForm';
import type {
  CreateQuestionFormData,
  CreateQuestionFormValidationErrors,
} from '../CreateQuestionForm/types';
import { getInitialCreateQuestionFormData } from '../CreateQuestionForm/types';

export interface CreateQuestionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateQuestionFormData) => void | Promise<void>;
  difficultyOptions: { value: string; label: string }[];
  categoryOptions: { value: string; label: string }[];
  courseOptions?: { value: string; label: string }[];
  saving?: boolean;
  validationErrors?: CreateQuestionFormValidationErrors;
  /** Dados iniciais para preencher o formulário (ex.: ao editar). Se passado quando open=true, o form é preenchido em vez de resetado. */
  initialData?: CreateQuestionFormData | null;
  /** Exibir estado de carregamento no corpo do modal (ex.: ao buscar questão para edição). */
  loading?: boolean;
}

export function CreateQuestionModal({
  open,
  onClose,
  onSave,
  difficultyOptions,
  categoryOptions,
  courseOptions = [],
  saving = false,
  validationErrors,
  initialData,
  loading = false,
}: CreateQuestionModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [formData, setFormData] = useState<CreateQuestionFormData>(
    getInitialCreateQuestionFormData
  );

  useEffect(() => {
    if (open) {
      setFormData(
        initialData != null ? initialData : getInitialCreateQuestionFormData()
      );
    }
  }, [open, initialData]);

  const handleSave = useCallback(
    async (data: CreateQuestionFormData) => {
      await onSave(data);
      onClose();
    },
    [onSave, onClose]
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  const overlayStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: theme.spacing.lg,
    }),
    [theme.spacing.lg]
  );

  const modalStyle: React.CSSProperties = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius.lg,
      boxShadow:
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    }),
    [theme.palette.background.paper, theme.shape.borderRadius.lg]
  );

  const headerStyle: React.CSSProperties = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing.lg,
      marginBottom: 0,
      flexShrink: 0,
    }),
    [theme.spacing.lg]
  );

  const titleStyle: React.CSSProperties = useMemo(
    () => ({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.palette.text.primary,
      margin: 0,
    }),
    [theme.typography, theme.palette.text.primary]
  );

  const closeButtonStyle: React.CSSProperties = useMemo(
    () => ({
      background: 'none',
      border: 'none',
      padding: theme.spacing.xs,
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.shape.borderRadius.sm,
      lineHeight: 1,
      fontSize: '1.25rem',
    }),
    [
      theme.spacing.xs,
      theme.palette.text.secondary,
      theme.shape.borderRadius.sm,
    ]
  );

  const bodyStyle: React.CSSProperties = useMemo(
    () => ({
      padding: `${theme.spacing.sm} ${theme.spacing.lg} ${theme.spacing.lg}`,
      overflow: 'auto',
      flex: 1,
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    }),
    [theme.spacing.sm, theme.spacing.lg]
  );

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-question-modal-title"
      style={overlayStyle}
      onKeyDown={handleKeyDown}
    >
      <style>{`.create-question-modal-scroll::-webkit-scrollbar { display: none; }`}</style>
      <div
        className="create-question-modal-scroll"
        style={modalStyle}
      >
        <div style={headerStyle}>
          <h2 id="create-question-modal-title" style={titleStyle}>
            {t('questionBank.create.modal_title') ?? 'Create New Question'}
          </h2>
          <button
            type="button"
            style={closeButtonStyle}
            onClick={onClose}
            aria-label={t('questionBank.create.close_aria') ?? 'Close'}
          >
            ×
          </button>
        </div>
        <div style={bodyStyle}>
          {loading ? (
            <p style={{ margin: 0, color: theme.palette.text.secondary }}>
              {t('common.loading') ?? 'Loading...'}
            </p>
          ) : (
            <CreateQuestionForm
              data={formData}
              onChange={setFormData}
              difficultyOptions={difficultyOptions}
              categoryOptions={categoryOptions}
              courseOptions={courseOptions}
              onCancel={handleCancel}
              onSave={handleSave}
              saving={saving}
              validationErrors={validationErrors}
            />
          )}
        </div>
      </div>
    </div>
  );
}
